import { Trans, useLingui } from "@lingui/react/macro";

import { cn } from "@/utils/shadcn-ui";

import type { GDMTResult } from "../../../api";

import { Badge } from "../../../components/ui-kit/base/badge";
import { missingForScore, optimizationColor, optimizedPercent } from "./gdmt";
import { GdmtBreakdown } from "./gdmt-breakdown";
import { GdmtScoreRing } from "./gdmt-score-ring";

export function GdmtCard({ result }: { result: GDMTResult }) {
  const { i18n } = useLingui();
  // Say what's missing rather than rendering nothing, so the score's absence is
  // explained on the tab where the medications are edited.
  const missing = missingForScore(result);
  if (missing) {
    return (
      <div className="bg-card mb-6 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border p-4">
        <span className="text-card-foreground font-semibold">
          <Trans>GDMT Optimization</Trans>
        </span>
        <span className="text-muted-foreground text-sm">{i18n._(missing)}</span>
      </div>
    );
  }

  const percent = optimizedPercent(result);
  const color = optimizationColor(percent);

  return (
    <div className="bg-card mb-6 rounded-lg border p-4">
      <div className="flex items-start gap-3 sm:items-center sm:gap-4">
        <GdmtScoreRing
          score={result.gdmt_score}
          max={result.gdmt_max}
          size={72}
        />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-card-foreground font-semibold">
              <Trans>GDMT Optimization</Trans>
            </span>
            {result.hf_type && (
              <Badge variant="secondary">{result.hf_type}</Badge>
            )}
          </div>
          <p className="text-muted-foreground text-sm">
            <Trans>
              {result.gdmt_score} of {result.gdmt_max} points ·
              guideline-concordant therapy across the foundational drug classes.
            </Trans>
          </p>
        </div>

        <div className="shrink-0 text-right">
          <div className={cn("text-xl font-bold sm:text-2xl", color.text)}>
            {percent}%
          </div>
          <div className="text-muted-foreground text-xs">
            <Trans>optimized</Trans>
          </div>
        </div>
      </div>

      <hr className="my-3" />

      <GdmtBreakdown result={result} />
    </div>
  );
}
